import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, Tab, TextField, FormControl, InputLabel, Select, MenuItem,
    Card, CardContent, Divider, Chip, ListItemIcon, Tooltip, Grow
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';

import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function ITSupplierDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [supplier, setSupplier] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    
    // Conversation
    const [conversations, setConversations] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    
    // Files
    const [files, setFiles] = useState([]);
    
    // Contracts
    const [contracts, setContracts] = useState([]);
    const [openContract, setOpenContract] = useState(false);
    const [newContract, setNewContract] = useState({ name: '', start_date: '', end_date: '', status: 'Active', file: null });

    // Contacts
    const [contacts, setContacts] = useState([]);
    const [openContact, setOpenContact] = useState(false);
    const [newContact, setNewContact] = useState({ name: '', position: '', email: '', phone: '' });

    // Edit Mode
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSupplier();
        fetchCountries();
        fetchConversations();
        fetchFiles();
        fetchContracts();
        fetchContacts();
    }, [id]);

    const fetchContacts = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/contacts`, { headers });
            setContacts(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchSupplier = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}`, { headers });
            setSupplier(res.data);
            setEditData(res.data);
            setLoading(false);
        } catch (e) { console.error(e); setLoading(false); }
    };

    const fetchConversations = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/conversations`, { headers });
            setConversations(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/files`, { headers });
            setFiles(res.data);
        } catch (e) {console.error(e);}
    };

    const fetchContracts = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers/${id}/contracts`, { headers });
            setContracts(res.data);
        } catch (e) {console.error(e);}
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API_URL}/it-suppliers/${id}`, editData, { headers });
            await fetchSupplier();
            setEditMode(false);
        } catch (e) {console.error(e);}
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/conversations`, { message: newMessage }, { headers });
            setNewMessage('');
            fetchConversations();
        } catch (e) {console.error(e);}
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/files`, formData, { headers });
            fetchFiles();
        } catch (e) {
            console.error(e);
            alert("File upload failed: " + (e.response?.data?.error || e.message));
        }
    };

    const handleDeleteFile = async (fileId) => {
        if(!window.confirm("Delete this file?")) return;
        try {
            await axios.delete(`${API_URL}/it-suppliers/${id}/files/${fileId}`, { headers });
            fetchFiles();
        } catch (e) {console.error(e);}
    };

    const handleAddContract = async () => {
        const formData = new FormData();
        formData.append('name', newContract.name);
        formData.append('start_date', newContract.start_date);
        formData.append('end_date', newContract.end_date);
        formData.append('status', newContract.status);
        if (newContract.file) formData.append('file', newContract.file);

        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/contracts`, formData, { headers });
            setOpenContract(false);
            setNewContract({ name: '', start_date: '', end_date: '', status: 'Active', file: null });
            fetchContracts();
        } catch (e) {console.error(e);}
    };

    const handleAddContact = async () => {
        try {
            await axios.post(`${API_URL}/it-suppliers/${id}/contacts`, newContact, { headers });
            setOpenContact(false);
            setNewContact({ name: '', position: '', email: '', phone: '' });
            fetchContacts();
        } catch (e) {console.error(e);}
    };

    const handleDeleteContact = async (contact_id) => {
         if(!window.confirm("Delete this contact?")) return;
         try {
             await axios.delete(`${API_URL}/it-suppliers/${id}/contacts/${contact_id}`, { headers });
             fetchContacts();
         } catch (e) {console.error(e);}
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (loading) return <Loading />;
    if (!supplier) return <Typography>Supplier not found</Typography>;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, md: 4 }, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)' }}>
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 0 }} />
                <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 0 }} />
                
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/it-suppliers')}
                            sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', textTransform: 'none', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                        >
                            Back to IT Suppliers
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.2)', width: 52, height: 52, flexShrink: 0, fontSize: '1.3rem', fontWeight: 700, boxShadow: '0 6px 18px rgba(0,0,0,0.2)' }}>
                                {supplier.name ? supplier.name.charAt(0).toUpperCase() : <BusinessIcon />}
                            </Avatar>
                            <Typography variant="h5" fontWeight="900" color="white" sx={{ letterSpacing: 0.3, lineHeight: 1.3, wordBreak: 'break-word' }}>{supplier.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 0.5 }}>
                            <Chip label="Approved" icon={<CheckCircleIcon style={{ color: 'white' }} />} size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.25)', color: 'white', fontWeight: 700, border: '1px solid rgba(76, 175, 80, 0.5)', borderRadius: 1.5 }} />
                            <Chip icon={<BusinessIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label="IT Supplier" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            {supplier.country_name && (
                                <Chip icon={<FlagIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label={supplier.country_name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            )}
                            {supplier.location && (
                               <Chip icon={<LocationOnIcon fontSize="small" style={{ color: 'rgba(255,255,255,0.7)' }}/>} label={supplier.location} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 1.5 }} />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, pt: { xs: 0, md: 0 } }}>
                        {!editMode ? (
                            <Button
                                onClick={() => setEditMode(true)}
                                variant="contained"
                                startIcon={<EditIcon />}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#1e3c72',
                                    fontWeight: 'bold',
                                    px: 3,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                    '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s'
                                }}
                            >
                                Edit Details
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUpdate}
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', px: 3, borderRadius: 2, '&:hover': { bgcolor: '#388e3c' } }}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={() => { setEditMode(false); setEditData(supplier); }}
                                    variant="contained"
                                    startIcon={<CancelIcon />}
                                    sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 'bold', px: 3, borderRadius: 2, '&:hover': { bgcolor: '#d32f2f' } }}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
            </Grow>

            <Grow in={true} timeout={800}>
            <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f0f0f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 4, bgcolor: 'white' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' }, '& .Mui-selected': { color: 'primary.main' } }}>
                        <Tab icon={<BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Overview" />
                        <Tab icon={<PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Contacts" />
                        <Tab icon={<ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Conversations" />
                        <Tab icon={<FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Documents" />
                        <Tab icon={<GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }}/>} iconPosition="start" label="Contracts" />
                    </Tabs>
                </Box>

                {/* Overview Panel */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        {editMode ? (
                            <Grid item xs={12}>
                                <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>Edit Information</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField label="Name" fullWidth value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} variant="outlined" />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>Country</InputLabel>
                                                    <Select value={editData.country_id || ''} onChange={(e) => setEditData({...editData, country_id: e.target.value})} label="Country">
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField label="Location" fullWidth value={editData.location || ''} onChange={(e) => setEditData({...editData, location: e.target.value})} variant="outlined" />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField label="Phone" fullWidth value={editData.phone || ''} onChange={(e) => setEditData({...editData, phone: e.target.value})} variant="outlined" />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField label="Website" fullWidth value={editData.website || ''} onChange={(e) => setEditData({...editData, website: e.target.value})} variant="outlined" />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField label="Comment" fullWidth multiline rows={4} value={editData.comment || ''} onChange={(e) => setEditData({...editData, comment: e.target.value})} variant="outlined" />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12} md={8}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>About Supplier</Typography>
                                            <Typography variant="body1" sx={{ color: supplier.comment ? 'text.primary' : 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                                {supplier.comment || 'No comment/description provided for this supplier.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 3 }}>Contact Info</Typography>
                                            <List sx={{ p: 0 }}>
                                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                                    <ListItemIcon>
                                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 44, height: 44, boxShadow: '0 2px 10px rgba(59,130,246,0.15)' }}>
                                                            <LocationOnIcon />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={<Typography variant="caption" color="text.secondary" fontWeight="bold" letterSpacing={0.5} textTransform="uppercase">Location</Typography>} 
                                                        secondary={<Typography variant="body1" color="text.primary" fontWeight="medium">{supplier.location || '-'}</Typography>} 
                                                    />
                                                </ListItem>
                                                <Divider variant="inset" />
                                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                                    <ListItemIcon>
                                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 44, height: 44, boxShadow: '0 2px 10px rgba(59,130,246,0.15)' }}>
                                                            <PhoneIcon />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={<Typography variant="caption" color="text.secondary" fontWeight="bold" letterSpacing={0.5} textTransform="uppercase">Phone</Typography>} 
                                                        secondary={<Typography variant="body1" color="text.primary" fontWeight="medium">{supplier.phone || '-'}</Typography>} 
                                                    />
                                                </ListItem>
                                                <Divider variant="inset" />
                                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                                    <ListItemIcon>
                                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 44, height: 44, boxShadow: '0 2px 10px rgba(59,130,246,0.15)' }}>
                                                            <LanguageIcon />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={<Typography variant="caption" color="text.secondary" fontWeight="bold" letterSpacing={0.5} textTransform="uppercase">Website</Typography>} 
                                                        secondary={supplier.website ? <a href={supplier.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}>{supplier.website}</a> : '-'} 
                                                    />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </TabPanel>

                {/* Contacts Panel */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">Contacts ({contacts.length})</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenContact(true)} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)', textTransform: 'none' }}>
                            Add Contact
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {contacts.length === 0 ? (
                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                                    <PersonIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                    <Typography variant="h6" color="#64748b" fontWeight="bold">No Contacts found</Typography>
                                    <Typography color="text.secondary">Add contacts related to this supplier.</Typography>
                                </Paper>
                            </Grid>
                        ) : (
                            contacts.map(c => (
                                <Grid item xs={12} sm={6} md={4} key={c.id}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' } }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main', width: 50, height: 50, fontWeight: 'bold' }}>
                                                        {c.name ? c.name.charAt(0) : <PersonIcon />}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{c.position || 'No Title'}</Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteContact(c.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                                <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="body2">{c.email || '-'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="body2">{c.phone || '-'}</Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))
                        )}
                    </Grid>
                </TabPanel>

                {/* Conversations Panel */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '600px', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: 'white', overflow: 'hidden' }}>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {conversations.length === 0 ? (
                                <Box sx={{ m: 'auto', textAlign: 'center' }}>
                                    <ChatIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                    <Typography variant="h6" color="#64748b" fontWeight="bold">No conversations yet</Typography>
                                    <Typography color="text.secondary">Start the conversation below.</Typography>
                                </Box>
                            ) : (
                                conversations.map(c => (
                                    <Paper key={c.id} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', maxWidth: '80%', alignSelf: 'flex-start', bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary.main">{c.created_by_name || 'User'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
                                        </Box>
                                        <Typography variant="body1">{c.message}</Typography>
                                    </Paper>
                                ))
                            )}
                        </Box>
                        <Divider />
                        <Box sx={{ p: 2, display: 'flex', gap: 1, bgcolor: 'white' }}>
                            <TextField fullWidth placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} variant="outlined"  InputProps={{ sx: { borderRadius: 3 } }}/>
                            <Button variant="contained" endIcon={<SendIcon />} onClick={handleSendMessage} disabled={!newMessage.trim()} sx={{ borderRadius: 3, px: 4, background: newMessage.trim() ? 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)' : undefined }}>
                                Send
                            </Button>
                        </Box>
                    </Box>
                </TabPanel>

                {/* Documents Panel */}
                <TabPanel value={tabValue} index={3}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">Documents ({files.length})</Typography>
                        <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)', textTransform: 'none' }}>
                            Upload File
                            <input type="file" hidden onChange={handleFileUpload} />
                        </Button>
                    </Box>
                    
                    {files.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                            <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" color="#64748b" fontWeight="bold">No Documents found</Typography>
                            <Typography color="text.secondary">Upload related files such as PDFs, images, etc.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {files.map(f => (
                                <Grid item xs={12} sm={6} md={4} key={f.id}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', p: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' } }}>
                                        <Avatar sx={{ bgcolor: 'error.light', color: 'white', mr: 2 }}>
                                            <DescriptionIcon />
                                        </Avatar>
                                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                            <Typography variant="subtitle2" noWrap fontWeight="bold" title={f.filename}>{f.filename}</Typography>
                                            <Typography variant="caption" color="text.secondary">{new Date(f.created_at).toLocaleDateString()}</Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => window.open(`${API_URL}/files/download/${f.file_path}`)} sx={{ color: 'primary.main' }}>
                                            <DownloadIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteFile(f.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </TabPanel>

                {/* Contracts Panel */}
                <TabPanel value={tabValue} index={4}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">Contracts ({contracts.length})</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenContract(true)} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)', textTransform: 'none' }}>
                            Add Contract
                        </Button>
                    </Box>

                    {contracts.length === 0 ? (
                         <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
                            <GavelIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" color="#64748b" fontWeight="bold">No Contracts found</Typography>
                            <Typography color="text.secondary">Create and manage contracts for this supplier.</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {contracts.map(c => (
                                <Grid item xs={12} sm={6} md={4} key={c.id}>
                                    <Card elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' } }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#1976d2', fontWeight: 'bold' }}>
                                                        <GavelIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                                        <Chip size="small" label={c.status} color={c.status === 'Active' ? 'success' : 'default'} sx={{ mt: 0.5, fontWeight: 'bold' }} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Start: <b>{c.start_date || '-'}</b></Typography>
                                                <Typography variant="body2" color="text.secondary">End: <b>{c.end_date || '-'}</b></Typography>
                                            </Box>
                                            {c.file_path && (
                                                <Button size="small" startIcon={<DownloadIcon />} onClick={() => window.open(`${API_URL}/files/download/${c.file_path}`)} sx={{ mt: 2, textTransform: 'none', fontWeight: 'bold' }}>
                                                    Download Contract
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </TabPanel>
            </Paper>
            </Grow>

            {/* Dialog for New Contact */}
             <Dialog open={openContact} onClose={() => setOpenContact(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>New Contact</DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Name" fullWidth value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Position" fullWidth value={newContact.position} onChange={(e) => setNewContact({...newContact, position: e.target.value})} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Email" fullWidth value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Phone" fullWidth value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenContact(false)} color="inherit" sx={{ fontWeight: 'bold' }}>Cancel</Button>
                    <Button onClick={handleAddContact} variant="contained" disabled={!newContact.name} sx={{ borderRadius: 2 }}>Create Contact</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for New Contract */}
            <Dialog open={openContract} onClose={() => setOpenContract(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>New Contract</DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField label="Contract Name" fullWidth value={newContract.name} onChange={(e) => setNewContract({...newContract, name: e.target.value})} sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newContract.start_date} onChange={(e) => setNewContract({...newContract, start_date: e.target.value})} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="End Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newContract.end_date} onChange={(e) => setNewContract({...newContract, end_date: e.target.value})} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={newContract.status} onChange={(e) => setNewContract({...newContract, status: e.target.value})} label="Status">
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Expired">Expired</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth sx={{ py: 1.5, borderStyle: 'dashed' }}>
                                {newContract.file ? newContract.file.name : "Upload Contract File"}
                                <input type="file" hidden onChange={(e) => setNewContract({...newContract, file: e.target.files[0]})} />
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenContract(false)} color="inherit" sx={{ fontWeight: 'bold' }}>Cancel</Button>
                    <Button onClick={handleAddContract} variant="contained" disabled={!newContract.name} sx={{ borderRadius: 2 }}>Create Contract</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default ITSupplierDetails;
